import { useCallback, useMemo } from "react";
import { Button, Loader } from "@storybook";

import { Card, Description, Label, LabelDescription } from "components";
import { useInstance, useTranslation } from "hooks";

import "./signin-methods.scss";
import { SignInMethodSkelton } from "./skeltons";
import { useSignin } from "views/signin/stores";
import { getRandomColor } from "utils/color";
import GlobalInstanceManager from "instance-manger";

interface ISigninMethods {
  handleBack: () => void; // Function to handle going back
  handleOption: (e: any) => Promise<void>; // Function to handle selecting an option
}

/**
 * SigninMethods Component
 * @param {Object} props - Component props
 * @param {() => void} props.handleBack - Function to handle going back
 * @param {(e: any) => void} props.handleOption - Function to handle selecting an option
 */
export const SigninMethods = ({ handleBack, handleOption }: ISigninMethods) => {
  // Hook to get translation functions
  const { t } = useTranslation();
  const {
    verified_user,
    handleOtherAccount,
    isVerfiedUser,
    setLoadingMethodSelection,
    loadingMethodSelection,
  } = useSignin();

  // State to manage loading status and index of the loading element

  const { support_signin_methods } = GlobalInstanceManager.webcomponentProps;
  const { instanceDetails } = useInstance();

  /**
   * Handles changing the selected option
   * @param {any} el - The selected option
   * @param {number} index - The index of the selected option
   */
  const handleChange = useCallback(
    async (el: any, index: number) => {
      setLoadingMethodSelection({
        index: index,
        loading: true,
      });

      await handleOption(el);
    },
    [handleOption, setLoadingMethodSelection]
  );

  const methods = useMemo(() => {
    if (t("signin.methods.data")) {
      return t?.("signin.methods.data")?.filter((el: any) =>
        (instanceDetails as any).methods[el.value] && !support_signin_methods
          ? true
          : support_signin_methods.includes(el.value)
      );
    } else {
      return [];
    }
  }, [instanceDetails, support_signin_methods, t]);

  if (!methods?.length) {
    return <SignInMethodSkelton />;
  }

  return (
    <Card
      className="flex direction-col gap-16 relative sm-border-0"
      isLoading={loadingMethodSelection.loading}
    >
      <>
        {isVerfiedUser && (
          <div className="signin-methods-wrapper__img-wrapper">
            {verified_user.userImage ? (
              <img src={verified_user.userImage} alt="userImage" />
            ) : (
              <div
                className="signin-methods-wrapper__profile_bg_color"
                style={{
                  backgroundColor: getRandomColor(),
                }}
              >
                {verified_user.name?.charAt(0).toUpperCase()}
              </div>
            )}

            {verified_user.name && (
              <Label
                content={
                  <div className="d-flex direction-col gap-8 ">
                    <div className="fs-14 fw-500">Welcome,</div>
                    <div>{verified_user.name}</div>
                  </div>
                }
              />
            )}
          </div>
        )}
        {/* Label and description for the signin methods */}
        <LabelDescription
          label={t("signin.methods.label")}
          description={t("signin.methods.sublabel")}
        />

        {/* List of signin method options */}
        {methods?.map((el: any, index: number) => {
          return (
            <div
              key={el.value}
              className={`signin-methods-wrapper__option ${
                loadingMethodSelection.loading
                  ? "signin-methods-wrapper__option--disabled"
                  : ""
              }`}
              onClick={() =>
                !loadingMethodSelection.loading ? handleChange(el, index) : {}
              }
            >
              <div className="signin-methods-wrapper__icon">
                <i className={el.icon} />
              </div>

              <div className="signin-methods-wrapper__label">{el.label}</div>

              <div className="signin-methods-wrapper__left-icon">
                {loadingMethodSelection.loading &&
                loadingMethodSelection.index === index ? (
                  <Loader dimension={20} type="loader" />
                ) : (
                  <i className="ri-arrow-right-s-line" />
                )}
              </div>
            </div>
          );
        })}

        {/* Back button */}
        {!isVerfiedUser && (
          <Button
            label="Back"
            type="button__filled button__filled--secondary-hover  button__block"
            handleClick={handleBack}
          />
        )}

        {isVerfiedUser && (
          <Description
            content={
              <div className="text-center" onClick={handleOtherAccount}>
                Not {verified_user.name} ?<span> Add Another User</span>
                {/* Clickable text to navigate to sign-in */}
              </div>
            }
            className="text-center signin-methods-wrapper__footer" // Center aligned text
          />
        )}
      </>
    </Card>
  );
};
